'use client';

import { useCallback, useMemo, useSyncExternalStore } from 'react';
import { composerUploadProgress } from '@/lib/uploads/composer-upload-progress';

/**
 * Live bytes this device has sent for the given uploads, from the shared
 * composer progress store (#4809). The snapshot is a string, so React only
 * re-renders when one of these uploads actually moved.
 */
export function useLocalUploadBytes(
  uploadIds: readonly string[]
): Record<string, number | undefined> {
  const key = uploadIds.join('\n');
  const snapshot = useCallback(
    () =>
      key
        .split('\n')
        .map((uploadId) => {
          const entry = uploadId ? composerUploadProgress.get(uploadId) : null;
          return entry ? String(entry.loadedBytes) : '';
        })
        .join('\n'),
    [key]
  );
  const value = useSyncExternalStore(
    composerUploadProgress.subscribe,
    snapshot,
    () => ''
  );
  return useMemo(() => {
    const ids = key ? key.split('\n') : [];
    const bytes = value.split('\n');
    const result: Record<string, number | undefined> = {};
    ids.forEach((uploadId, index) => {
      const raw = bytes[index];
      result[uploadId] = raw ? Number(raw) : undefined;
    });
    return result;
  }, [key, value]);
}
